import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getAuthToken } from "../../lib/api/tokenStore";
import { getApiErrorMessage } from "../../lib/api/apiError";
import { showErrorToast, showInfoToast, showSuccessToast } from "../../components/Toast";
import useCart from "../cart/useCart";
import EmptyCartView from "../cart/components/EmptyCartView";
import useProfile from "../profile/useProfile";
import useDeliverySelection from "./useDeliverySelection";
import useDeliveryAddress from "./useDeliveryAddress";
import useCheckoutQuote from "./useCheckoutQuote";
import useCheckoutOrder from "./useCheckoutOrder";
import usePaymentMethods from "./usePaymentMethods";
import useCodPayment from "./useCodPayment";
import useWalletPayment from "./useWalletPayment";
import useWalletTopUp from "./useWalletTopUp";
import QUERY_TAGS from "../search/queryTags";
import { invalidateTags } from "../search/queryCache";
import { invalidateOrders } from "../orders/ordersCache";
import { isInsufficientFunds, isStaleOrderAmount } from "./paymentsMappers";
import { settlePaystackOrder, startPaystackPayment } from "./paystackPayment";
import {
  clearPendingPaystack,
  readPendingPaystack,
  referenceFromLocation,
} from "./paystackReturn";
import { launchPaystack } from "./paystackLauncher";
import { closePopup, openBlankPopup } from "./paystackPopup";
import { readPendingTopUp } from "./topUpReturn";
import blockingReason from "./checkoutGate";
import CheckoutSteps from "./components/CheckoutSteps";
import FlowStickyHeader from "./components/FlowStickyHeader";
import FlowPageHead from "./components/FlowPageHead";
import DeliveryAddressCard from "./components/DeliveryAddressCard";
import PaymentMethodCard from "./components/PaymentMethodCard";
import ReservationTimer from "./components/ReservationTimer";
import VoucherCard from "./components/VoucherCard";
import CheckoutSummary from "./components/CheckoutSummary";
import PopupBlockedDialog from "./components/PopupBlockedDialog";

import "../cart/styles.css";
import "./styles.css";

const CheckoutRoute = () => {
  const navigate = useNavigate();
  const { profile } = useProfile();

  const cart = useCart({ enabled: Boolean(getAuthToken()) });
  const delivery = useDeliverySelection(cart.summary.carts);
  const addressState = useDeliveryAddress();
  const [couponInput, setCouponInput] = useState("");
  const [payError, setPayError] = useState(null);
  const [isPaying, setIsPaying] = useState(false);
  const [topUpOffer, setTopUpOffer] = useState(null);
  const [pendingPaystack, setPendingPaystack] = useState(() => readPendingPaystack());

  const quoteState = useCheckoutQuote({
    carts: cart.summary.carts,
    deliveryTypeIdForCart: delivery.deliveryTypeIdForCart,
    address: addressState.hasCoordinates ? addressState.address : null,
  });

  const order = useCheckoutOrder({
    carts: cart.summary.carts,
    deliveryTypeIdForCart: delivery.deliveryTypeIdForCart,
    address: addressState.hasCoordinates ? addressState.address : null,
    couponCode: quoteState.couponCode,
  });

  const payment = usePaymentMethods({
    currency: order.reservation?.payableAmount?.currency || cart.summary.currency,
  });
  const cod = useCodPayment();
  const wallet = useWalletPayment();
  const topUp = useWalletTopUp();

  const reason = blockingReason({ summary: cart.summary, delivery, address: addressState });

  const checkoutParamsSettled = useRef(false);

  useEffect(() => {
    if (!checkoutParamsSettled.current) {
      checkoutParamsSettled.current = true;

      return;
    }

    if (!order.reservation) return;

    order.dropReservation();
    clearPendingPaystack();
    setPendingPaystack(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delivery.selectedOption, addressState.address, quoteState.couponCode]);

  const handleSelectPaymentMethod = (code) => {
    if (code !== payment.selectedCode) {
      if (order.reservation) order.dropReservation();
      cod.reset();
      clearPendingPaystack();
      setPendingPaystack(null);
      setTopUpOffer(null);
      setPayError(null);
    }

    payment.selectMethod(code);
  };

  const handleApplyCoupon = () => {
    if (!couponInput.trim()) return;

    quoteState.applyCoupon(couponInput.trim());
  };

  const handleRemoveCoupon = () => {
    setCouponInput("");
    quoteState.removeCoupon();
  };

  const handleContinue = async () => {
    setPayError(null);
    await order.reserve();
  };

  const placedMessage = (placed) => {
    const count = placed.subOrderCount || cart.summary.carts.length;

    return count > 1
      ? `Order #${placed.orderNumber} is split into ${count} supplier orders. Track each one here.`
      : `Order #${placed.orderNumber} has been placed. Track its progress here.`;
  };

  const finalizePlacement = (result) => {
    if (result.status === "placed") {
      cart.clearCart();
      invalidateTags([
        QUERY_TAGS.CART,
        QUERY_TAGS.PAYMENT_WALLET,
        QUERY_TAGS.PAYMENT_TRANSACTIONS,
      ]);
      invalidateOrders();
      showSuccessToast(placedMessage(result.order));
      navigate("/my-orders", { replace: true });

      return true;
    }

    if (result.status === "expired") {
      setPayError("Your reservation expired. Please reserve again.");

      return true;
    }

    return false;
  };

  const forgetPaystack = () => {
    clearPendingPaystack();
    setPendingPaystack(null);
  };

  const resolvePaystack = async ({ reference, trusted }) => {
    setPayError(null);
    setIsPaying(true);

    try {
      const result = await settlePaystackOrder({
        reference,
        trusted,
        place: order.place,
        reserve: order.reserve,
      });

      if (result.status === "failed") {
        forgetPaystack();
        setPayError("The payment failed. Please try again.");

        return;
      }

      if (result.status === "placed") {
        forgetPaystack();
        finalizePlacement(result);

        return;
      }

      if (result.status === "expired") {
        setPayError("Your reservation expired. We will reserve it again — your payment is safe.");

        return;
      }

      setPayError(
        getApiErrorMessage(
          result.error,
          "We could not confirm your order. Your payment is safe — please try again."
        )
      );
    } finally {
      setIsPaying(false);
    }
  };

  const reportTopUp = (outcome) => {
    if (outcome.status === "credited") {
      setTopUpOffer(null);
      showSuccessToast("Your wallet has been topped up. You can place your order now.");

      return;
    }

    if (outcome.status === "pending") {
      showInfoToast(
        "We have not seen the payment land yet. Your balance will update as soon as it clears."
      );
    }
  };

  useEffect(() => {
    const returnedReference = referenceFromLocation(window.location.search);

    if (!returnedReference) return;

    const stripQuery = () => window.history.replaceState({}, "", window.location.pathname);
    const pendingTopUp = readPendingTopUp();

    if (pendingTopUp && pendingTopUp.reference === returnedReference) {
      const resumeTopUp = async () => {
        setIsPaying(true);

        try {
          const { confirmed } = await topUp.confirm(pendingTopUp);

          reportTopUp({ status: confirmed ? "credited" : "pending" });
        } finally {
          setIsPaying(false);
          stripQuery();
        }
      };

      resumeTopUp();

      return;
    }

    const pending = readPendingPaystack();

    if (!pending || pending.reference !== returnedReference) return;

    stripQuery();
    resolvePaystack({ reference: returnedReference, trusted: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTopUp = async () => {
    if (!topUpOffer) return;

    setPayError(null);

    if (!profile?.email) {
      setPayError("Add an email address to your profile before paying.");

      return;
    }

    try {
      reportTopUp(
        await topUp.start({
          walletId: topUpOffer.walletId,
          currencyCode: topUpOffer.currencyCode,
          amount: topUpOffer.shortfall.value,
          balanceBefore: topUpOffer.balanceBefore,
        })
      );
    } catch (err) {
      showErrorToast(getApiErrorMessage(err, "Could not start the top-up. Please try again."));
    }
  };

  const handleResumeTopUp = async () => {
    try {
      reportTopUp(await topUp.resumeBlocked());
    } catch (err) {
      showErrorToast(getApiErrorMessage(err, "Could not start the top-up. Please try again."));
    }
  };

  const handleAlreadyPaid = async () => {
    const pending = readPendingPaystack();

    if (!pending?.reference) {
      setPendingPaystack(null);
      setPayError("We could not find that payment. Please contact support if you were charged.");

      return;
    }

    await resolvePaystack({ reference: pending.reference, trusted: false });
  };

  const payWithPaystack = async (amount) => {
    if (!profile?.email) {
      setPayError("Add an email address to your profile before paying.");

      return;
    }

    const popup = openBlankPopup();
    const supplierCount = cart.summary.carts.length;
    let initiation;

    try {
      initiation = await startPaystackPayment({
        amount,
        email: profile.email,
        description: `Gas order · ${supplierCount} supplier${supplierCount === 1 ? "" : "s"}`,
        context: {
          kind: "order",
          returnTo: "/checkout",
          pendingOrderId: order.reservation.pendingOrderId,
          orderKind: order.reservation.kind,
        },
      });
    } catch (err) {
      closePopup(popup);

      throw err;
    }

    setPendingPaystack(readPendingPaystack());

    const outcome = await launchPaystack(initiation, { popup, allowRedirect: true });

    if (outcome === "redirected") return;

    if (outcome === "cancelled" || outcome === "error") {
      forgetPaystack();

      return;
    }

    await resolvePaystack({ reference: initiation.reference, trusted: outcome === "success" });
  };

  const handlePay = async () => {
    if (!order.reservation) return;

    setPayError(null);
    setTopUpOffer(null);
    setIsPaying(true);

    try {
      const amount = order.reservation.payableAmount;
      let paymentPayload = null;

      if (payment.selectedCode === "COD") {
        const transaction = await cod.ensureTransaction(amount);

        if (!transaction) throw new Error("Could not set up cash on delivery. Please try again.");

        paymentPayload = { transactionIds: [transaction.id] };
      } else if (payment.selectedCode === "WALLET") {
        const outcome = await wallet.check({ amount: amount.value, currency: amount.currency });

        if (outcome.status === "insufficient") {
          setPayError(
            `Your wallet is short by ${outcome.shortfall.currency} ${outcome.shortfall.value.toFixed(2)}.`
          );
          setTopUpOffer({
            walletId: outcome.wallet.id,
            currencyCode: outcome.wallet.currencyCode,
            shortfall: outcome.shortfall,
            balanceBefore: outcome.wallet.balance.value,
          });

          return;
        }

        if (outcome.status !== "ok") {
          setPayError("Your wallet could not be charged. Please try another method.");

          return;
        }

        const stopOn = (err) => isInsufficientFunds(err) || isStaleOrderAmount(err);
        paymentPayload = { walletCurrency: outcome.walletCurrency };

        let result = await order.place(paymentPayload, { stopOn });

        if (result.status === "stopped" && isStaleOrderAmount(result.error)) {
          const reserved = await order.reserve();

          if (reserved.status === "reserved") {
            result = await order.place(paymentPayload, {
              stopOn,
              reservationOverride: reserved.reservation,
            });
          }
        }

        if (result.status === "stopped") {
          setPayError(
            isInsufficientFunds(result.error)
              ? "Your wallet could not cover this payment. Please top up and try again."
              : "The order total changed while you were paying. Please review your order and try again."
          );

          return;
        }

        if (finalizePlacement(result)) return;

        setPayError(getApiErrorMessage(result.error, "Could not place your order. Please try again."));

        return;
      } else {
        await payWithPaystack(amount);

        return;
      }

      const result = await order.place(paymentPayload);

      if (finalizePlacement(result)) return;

      setPayError(getApiErrorMessage(result.error, "Could not place your order. Please try again."));
    } catch (err) {
      showErrorToast(getApiErrorMessage(err, "Could not process your payment. Please try again."));
    } finally {
      setIsPaying(false);
    }
  };

  const showEmptyState =
    !cart.isLoading && cart.summary.isEmpty && !isPaying && !payError;

  return (
    <div className="sy-flow sy-checkout full-bleed">
      <div className="sy-flow-shell">
        <FlowStickyHeader>
          <FlowPageHead
            title="Checkout"
            back={
              <button
                type="button"
                className="sy-flow-back"
                onClick={() => {
                  forgetPaystack();
                  navigate("/my-cart");
                }}
              >
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M10 3 5 8l5 5" />
                </svg>
                Back to cart
              </button>
            }
          />

          <CheckoutSteps current="checkout" />
        </FlowStickyHeader>

        {showEmptyState ? (
          <EmptyCartView />
        ) : (
          <div className="sy-flow-grid">
            <div className="sy-flow-main">
              {order.reservation && (
                <ReservationTimer
                  secondsLeft={order.secondsLeft}
                  ttlSeconds={order.ttlSeconds}
                />
              )}

              <DeliveryAddressCard
                summary={cart.summary}
                delivery={delivery}
                addressState={addressState}
              />

              <VoucherCard
                value={couponInput}
                applied={quoteState.couponCode}
                message={quoteState.couponMessage}
                onChange={setCouponInput}
                onApply={handleApplyCoupon}
                onRemove={handleRemoveCoupon}
              />

              {order.reservation && (
                <PaymentMethodCard
                  payment={{ ...payment, selectMethod: handleSelectPaymentMethod }}
                />
              )}
            </div>

            <div className="sy-flow-rail">
              <CheckoutSummary
                summary={cart.summary}
                quote={quoteState.quote}
                isQuoting={quoteState.isQuoting}
              >
                {order.reservation ? (
                  <>
                    <button
                      type="button"
                      className="sy-flow-btn sy-flow-btn-primary sy-flow-block sy-checkout-cta"
                      disabled={isPaying || order.secondsLeft === 0}
                      onClick={handlePay}
                    >
                      {isPaying
                        ? "Working…"
                        : payment.selectedCode === "PAYSTACK"
                        ? "Proceed to payment"
                        : "Place order"}
                    </button>

                    {payError && <p className="sy-flow-note">{payError}</p>}

                    {pendingPaystack && !topUpOffer && (
                      <button
                        type="button"
                        className="sy-flow-btn sy-flow-btn-ghost sy-flow-block"
                        disabled={isPaying}
                        onClick={handleAlreadyPaid}
                      >
                        I have already paid
                      </button>
                    )}

                    {topUpOffer && (
                      <button
                        type="button"
                        className="sy-flow-btn sy-flow-btn-ghost sy-flow-block"
                        disabled={topUp.isStarting}
                        onClick={handleTopUp}
                      >
                        {topUp.isStarting ? "Working…" : "Top Up Wallet"}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="sy-flow-btn sy-flow-btn-primary sy-flow-block sy-checkout-cta"
                      disabled={Boolean(reason)}
                      onClick={handleContinue}
                    >
                      Continue to payment
                    </button>

                    {reason && <p className="sy-flow-note">{reason}</p>}
                    {payError && <p className="sy-flow-note">{payError}</p>}
                  </>
                )}
              </CheckoutSummary>
            </div>
          </div>
        )}
      </div>

      {topUp.blocked && (
        <PopupBlockedDialog
          busy={topUp.isStarting}
          onRetry={handleResumeTopUp}
          onCancel={topUp.dismissBlocked}
        />
      )}
    </div>
  );
};

export default CheckoutRoute;
